import { useCallback } from "react";
import { ORG_PERMISSIONS } from "@/api/queries/orgPermissions";
import { ApolloError, useQuery } from "@apollo/client";
import { useOrg } from "./useOrg";
import {
  OrganizationPartnershipLevelEnum,
  PartnerFeatureEnum,
} from "@/__generated__/types";

export function useOrgPermissions(): {
  loading: boolean;
  error: ApolloError | undefined;
  hasOrgFeature: (flag: PartnerFeatureEnum) => boolean;
  hasOrgPartnershipLevel: (level: OrganizationPartnershipLevelEnum) => boolean;
  hasMinOrgPartnershipLevel: (
    minLevel: OrganizationPartnershipLevelEnum,
  ) => boolean;
  getOrgFeatureFlags: () => PartnerFeatureEnum[] | null;
  getOrgPartnershipLevel: () => OrganizationPartnershipLevelEnum | null;
} {
  const { org, loading: orgLoading, error: orgError } = useOrg();

  // Load permissions for the current organization
  const {
    data: permissionsData,
    loading,
    error,
  } = useQuery(ORG_PERMISSIONS, {
    variables: {
      id: org?.id || "",
    },
    skip: !org?.id,
  });

  let featureFlags: PartnerFeatureEnum[] | null = null;
  let partnershipLevel: OrganizationPartnershipLevelEnum | null = null;
  if (
    permissionsData?.node &&
    permissionsData.node.__typename === "Organization"
  ) {
    featureFlags = permissionsData.node.permissions?.flags || [];
    partnershipLevel =
      permissionsData.node.permissions?.partnershipLevel || null;
  }

  const hasOrgFeature = useCallback(
    (flag: PartnerFeatureEnum): boolean => {
      return featureFlags?.includes(flag) || false;
    },
    [featureFlags],
  );

  const hasOrgPartnershipLevel = useCallback(
    (level: OrganizationPartnershipLevelEnum): boolean => {
      return partnershipLevel ? partnershipLevel === level : false;
    },
    [partnershipLevel],
  );

  const hasMinOrgPartnershipLevel = useCallback(
    (minLevel: OrganizationPartnershipLevelEnum): boolean => {
      // Allow null|undefined for the sales team
      if (partnershipLevel === null || partnershipLevel === undefined)
        return true;

      const levelHierarchy: OrganizationPartnershipLevelEnum[] = [
        OrganizationPartnershipLevelEnum.Lite,
        OrganizationPartnershipLevelEnum.Pro,
        OrganizationPartnershipLevelEnum.Premier,
      ];

      const orgLevelIndex = levelHierarchy.indexOf(partnershipLevel);
      const minLevelIndex = levelHierarchy.indexOf(minLevel);

      return orgLevelIndex >= minLevelIndex;
    },
    [partnershipLevel],
  );

  /**
   * Get all organization feature flags
   * @returns array of flags, or null if not loaded/error
   */
  const getOrgFeatureFlags = useCallback((): PartnerFeatureEnum[] | null => {
    return featureFlags;
  }, [featureFlags]);

  /**
   * Get the current organization's partnership level
   * @returns partnership level or null if not loaded/error
   */
  const getOrgPartnershipLevel =
    useCallback((): OrganizationPartnershipLevelEnum | null => {
      return partnershipLevel;
    }, [partnershipLevel]);

  return {
    loading: loading || orgLoading,
    error: error || orgError,
    hasOrgFeature,
    hasOrgPartnershipLevel,
    hasMinOrgPartnershipLevel,
    getOrgFeatureFlags,
    getOrgPartnershipLevel,
  };
}
